import { useEffect } from 'react';
import { motion } from 'framer-motion';

// Shared easing + reveal variants for a cohesive motion system
export const EASE = [0.16, 1, 0.3, 1];

export const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: EASE, delay: i * 0.08 },
  }),
};

export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
};

// Reusable scroll reveal wrapper
export const Reveal = ({ children, className = '', delay = 0, y = 40, as = 'div' }) => {
  const M = motion[as] || motion.div;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </M>
  );
};

// Production canonical domain (migration target — preserves SEO on banmorecore.com)
export const SITE_URL = 'https://www.banmorecore.com';
const DEFAULT_OG = `${SITE_URL}/wm/images_heroHome.webp`;

const upsertMeta = (selector, attr, key, content) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

// Full per-page SEO: title, description, keywords, canonical, Open Graph, Twitter Card
export const useSeo = (title, description, opts = {}) => {
  useEffect(() => {
    const path = window.location.pathname;
    const canonical = `${SITE_URL}${path === '/' ? '/' : path.replace(/\/$/, '')}`;
    const image = opts.image || DEFAULT_OG;

    document.title = title;
    if (description) upsertMeta('meta[name="description"]', 'name', 'description', description);
    if (opts.keywords)
      upsertMeta('meta[name="keywords"]', 'name', 'keywords', opts.keywords);

    upsertLink('canonical', canonical);

    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    if (description)
      upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', opts.type || 'website');
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', image);
    upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'Banmore Electricals');

    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    if (description)
      upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', image);

    window.scrollTo(0, 0);
  }, [title, description, opts.keywords, opts.image, opts.type]);
};

// Inject/replace a JSON-LD structured-data block by id
export const JsonLd = ({ id, data }) => {
  useEffect(() => {
    const scriptId = `ld-${id}`;
    let el = document.getElementById(scriptId);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = scriptId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const node = document.getElementById(scriptId);
      if (node) node.remove();
    };
  }, [id, data]);
  return null;
};
